import { useEffect, useState, ChangeEvent, FormEvent } from "react";

import Modal from "../../tailadmin/components/ui/Modal";
import BottomModal from "../ui/BottomModal";
import InputDefault from "../ui/InputDefault";

import { useAuth } from "../../context/AuthContext";
import { usePrescription } from "../../context/PrescriptionContext";
import { ExerciseSelect } from "../students/workout-plans/WorkoutPlanForm/ExerciseSelect";

import { Exercise, ExerciseInPlan } from "../../types";

interface PrescriptionExercicieModalProps {
  isOpen: boolean;
  data: Partial<ExerciseInPlan> | null;
  onBack: () => void;
}

/**
 * Estado do formulário usado no modal.
 * Mantém compatibilidade com o backend que espera reps/sets.
 */
interface ModalFormState extends Omit<Partial<Exercise>, "id"> {
  id: string;
  reps?: number | string;
  sets?: number | string;
  interval?: number | string;
  charge?: number | string;
}

function PrescriptionExercicieModal({
  isOpen,
  data,
  onBack,
}: PrescriptionExercicieModalProps) {
  const [form, setForm] = useState<ModalFormState>({
    id: "",
    reps: "",
    sets: "",
    interval: "",
    charge: "",
  });

  const [selectedExerciseName, setSelectedExerciseName] = useState("");

  const { getUser } = useAuth();
  const { saveExercicie } = usePrescription();
  const professionalId = String(getUser()?.id ?? "");

  useEffect(() => {
    if (!isOpen) return;

    if (data) {
      setForm({
        ...data,
        id: data.id ? String(data.id) : "",
      });
      setSelectedExerciseName(data.name ?? "");
    } else {
      setSelectedExerciseName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    saveExercicie({ ...form, id: Number(form.id) });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onBack}
      title="Exercício"
      className="!w-full !max-w-[600px]"
    >
      <div>
        <form onSubmit={handleSubmit} className="grid w-full p-4">
          <div className="w-full">
            {/* Exercício */}
            <div className="w-full grid gap-2 mt-4">
              <label
                htmlFor="id"
                className="pl-1 text-md font-medium text-black dark:text-white"
              >
                Exercício
              </label>

              {professionalId ? (
                <ExerciseSelect
                  professionalId={professionalId}
                  value={selectedExerciseName}
                  onChange={(name, id) => {
                    setSelectedExerciseName(name);
                    setForm((prev) => ({ ...prev, id: String(id) }));
                  }}
                />
              ) : (
                <p className="text-sm text-gray-500">Faça login para selecionar exercícios.</p>
              )}
            </div>

            {/* Reps / Sets */}
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <InputDefault
                label="Repetições"
                id="reps"
                name="reps"
                type="number"
                value={form.reps}
                onChange={handleChange}
              />

              <InputDefault
                label="Séries"
                id="sets"
                name="sets"
                type="number"
                value={form.sets}
                onChange={handleChange}
              />
            </div>

            {/* Intervalo / Carga */}
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <InputDefault
                label="Intervalo"
                id="interval"
                name="interval"
                type="text"
                value={form.interval}
                onChange={handleChange}
              />

              <InputDefault
                label="Carga"
                id="charge"
                name="charge"
                type="text"
                value={form.charge}
                onChange={handleChange}
              />
            </div>
          </div>
        </form>

        <div className="p-4 pb-2">
          <BottomModal
            onBack={onBack}
            id={form.id}
            onSave={() => saveExercicie({ ...form, id: Number(form.id) })}
          />
        </div>
      </div>
    </Modal>
  );
}

export default PrescriptionExercicieModal;
